import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usesHostSession } from '@/shared/bridge/env'
import { postToHost } from '@/shared/bridge/bridge'
import { getOmsUrl, type OmsOpenOptions } from '@/features/oms/bridge'
import { queryClient } from '@/shared/api/queryClient'
import { useAuthStore } from '@/shared/store/authStore'
import type { OmsLocale } from '@/features/oms/nav'
import { useNavigate } from 'react-router'

interface OmsFrameProps {
  options: OmsOpenOptions
  onTitleChange?: (title: string | null) => void
  onPathChange?: (path: string, title?: string) => void
  onModalStateChange?: (open: boolean) => void
  onBadgeChange?: (key: string, count: number) => void
  onLocaleChange?: (locale: OmsLocale) => void
  command?: OmsFrameCommand | null
}

export interface OmsFrameCommand {
  id: number
  type: 'OMS_GO_BACK' | 'NAVIGATE' | 'CLOSE_MODAL'
  path?: string
}

export function OmsFrame({
  options,
  onTitleChange,
  onPathChange,
  onModalStateChange,
  onBadgeChange,
  onLocaleChange,
  command,
}: OmsFrameProps) {
  const navigate = useNavigate()
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const retryTimersRef = useRef<number[]>([])
  const revealTimerRef = useRef<number | null>(null)
  const [loading, setLoading] = useState(true)
  const accessToken = useAuthStore((state) => state.accessToken)
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const src = useMemo(() => getOmsUrl(options), [options])
  const targetOrigin = useMemo(() => {
    try {
      return new URL(src, window.location.origin).origin
    } catch {
      return '*'
    }
  }, [src])

  const sendSession = useCallback(() => {
    const target = iframeRef.current?.contentWindow
    if (!target || !accessToken) return
    target.postMessage({ type: 'SET_TOKEN', payload: { accessToken } }, targetOrigin)
    target.postMessage(
      {
        type: 'SET_CONTEXT',
        payload: {
          source: 'qsc_web',
          user,
        },
      },
      targetOrigin,
    )
  }, [accessToken, targetOrigin, user])

  const sendSessionWithRetry = useCallback(() => {
    retryTimersRef.current.forEach((timer) => window.clearTimeout(timer))
    sendSession()
    retryTimersRef.current = [150, 500, 1_000].map((delay) => window.setTimeout(sendSession, delay))
  }, [sendSession])

  const revealFrame = useCallback(() => {
    if (revealTimerRef.current) window.clearTimeout(revealTimerRef.current)
    revealTimerRef.current = window.setTimeout(() => setLoading(false), 120)
  }, [])

  const handleHostSessionExit = useCallback((type: 'SESSION_EXPIRED' | 'LOGOUT') => {
    onModalStateChange?.(false)
    logout()
    queryClient.clear()

    if (usesHostSession()) {
      postToHost({ type })
      return
    }

    void navigate(`/login?reason=${type === 'LOGOUT' ? 'logout' : 'expired'}`, { replace: true })
  }, [logout, navigate, onModalStateChange])

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
        if (data?.type === 'READY') {
          sendSession()
          revealFrame()
        }
        if (
          (data?.type === 'TITLE_CHANGE' || data?.type === 'SET_TITLE' || data?.type === 'NAVIGATE') &&
          typeof data?.payload?.title === 'string'
        ) {
          onTitleChange?.(data.payload.title)
        }
        if (
          data?.type === 'NAVIGATE' &&
          typeof data?.payload?.path === 'string'
        ) {
          onPathChange?.(
            data.payload.path,
            typeof data.payload.title === 'string' ? data.payload.title : undefined,
          )
        }
        if (data?.type === 'MODAL_STATE' && typeof data?.payload?.open === 'boolean') {
          onModalStateChange?.(data.payload.open)
        }
        if (
          data?.type === 'BADGE_UPDATE' &&
          typeof data?.payload?.key === 'string' &&
          typeof data?.payload?.count === 'number'
        ) {
          onBadgeChange?.(data.payload.key, Math.max(0, data.payload.count))
        }
        if (
          data?.type === 'LOCALE_CHANGE' &&
          (data?.payload?.locale === 'ko' ||
            data?.payload?.locale === 'en' ||
            data?.payload?.locale === 'zh-TW')
        ) {
          onLocaleChange?.(data.payload.locale)
        }
        if (data?.type === 'SESSION_EXPIRED' || data?.type === 'LOGOUT') {
          handleHostSessionExit(data.type)
        }
        if (typeof data?.title === 'string') onTitleChange?.(data.title)
      } catch {
        // ignore non-bridge iframe messages
      }
    }

    window.addEventListener('message', listener)
    return () => {
      retryTimersRef.current.forEach((timer) => window.clearTimeout(timer))
      if (revealTimerRef.current) window.clearTimeout(revealTimerRef.current)
      window.removeEventListener('message', listener)
    }
  }, [
    handleHostSessionExit,
    onBadgeChange,
    onLocaleChange,
    onModalStateChange,
    onPathChange,
    onTitleChange,
    revealFrame,
    sendSession,
  ])

  useEffect(() => {
    if (!command) return
    iframeRef.current?.contentWindow?.postMessage(
      {
        type: command.type,
        payload: {
          source: 'qsc_web',
          ...(command.path ? { path: command.path } : {}),
        },
      },
      targetOrigin,
    )
  }, [command, targetOrigin])

  useEffect(() => {
    setLoading(true)
  }, [src])

  return (
    <div className="relative h-full min-h-full w-full bg-white">
      <iframe
        ref={iframeRef}
        title="OMS Web"
        src={src}
        tabIndex={-1}
        className="relative z-0 h-full min-h-full w-full border-0 bg-white focus:outline-none"
        allow="clipboard-read; clipboard-write"
        onLoad={() => {
          sendSessionWithRetry()
          window.setTimeout(revealFrame, 700)
        }}
      />
      {loading && (
        <div className="absolute inset-0 z-10 flex flex-col bg-bg px-5 py-5 lg:px-8 lg:py-7">
          <div className="mb-5 flex items-center justify-between">
            <div className="min-w-0">
              <div className="h-4 w-24 animate-pulse rounded-full bg-gray-200" />
              <div className="mt-3 h-7 w-44 animate-pulse rounded-md bg-gray-200" />
            </div>
            <div className="hidden h-9 w-28 animate-pulse rounded-full bg-gray-200 sm:block" />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="h-24 animate-pulse rounded-lg bg-white shadow-card" />
            <div className="h-24 animate-pulse rounded-lg bg-white shadow-card" />
            <div className="h-24 animate-pulse rounded-lg bg-white shadow-card" />
          </div>
          <div className="mt-5 grid min-h-0 flex-1 gap-4 lg:grid-cols-[1fr_18rem]">
            <div className="min-h-0 rounded-lg bg-white p-4 shadow-card">
              <div className="h-5 w-36 animate-pulse rounded bg-gray-200" />
              <div className="mt-5 space-y-3">
                {Array.from({ length: 7 }).map((_, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="size-10 animate-pulse rounded-md bg-gray-100" />
                    <div className="min-w-0 flex-1">
                      <div className="h-4 w-3/5 animate-pulse rounded bg-gray-100" />
                      <div className="mt-2 h-3 w-2/5 animate-pulse rounded bg-gray-100" />
                    </div>
                    <div className="h-6 w-14 animate-pulse rounded-full bg-gray-100" />
                  </div>
                ))}
              </div>
            </div>
            <div className="hidden rounded-lg bg-white p-4 shadow-card lg:block">
              <div className="h-5 w-28 animate-pulse rounded bg-gray-200" />
              <div className="mt-5 space-y-3">
                <div className="h-16 animate-pulse rounded-md bg-gray-100" />
                <div className="h-16 animate-pulse rounded-md bg-gray-100" />
                <div className="h-28 animate-pulse rounded-md bg-gray-100" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
